"use client"

import * as React from "react"
import { Select as SelectBase } from "@base-ui-components/react/select"
import { CheckIcon, ChevronDownIcon } from "lucide-react"

import { cn } from "@/lib/utils"

// Ref-based context to collect value→label mappings from SelectItem.
// Using a ref (not state) avoids re-render cascades that break Base UI's
// internal store and onValueChange tracking.
type ItemsMap = Record<string, React.ReactNode>

const SelectItemsRefContext = React.createContext<
  React.MutableRefObject<ItemsMap> | null
>(null)

function Select({
  children,
  value,
  defaultValue,
  onValueChange,
  open,
  defaultOpen,
  onOpenChange,
  disabled,
  required,
  name,
  items,
}: {
  children: React.ReactNode
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  required?: boolean
  name?: string
  /** Pre-populate value→label map so SelectValue can resolve labels
   *  even when the portal (and its SelectItems) are not mounted. */
  items?: Record<string, React.ReactNode>
}) {
  const itemsRef = React.useRef<ItemsMap>({})

  // Pre-populate labels from the items prop. This runs during render
  // (before children), so SelectValue can look up labels immediately.
  // When SelectItem mounts later (portal opens), it overwrites the
  // same keys — no conflict. Deliberately NOT moved to an effect (the
  // usual react-hooks/refs fix): an effect runs after commit, one render
  // too late for SelectValue to see the label on this same pass — the
  // whole point of populating it here.
  if (items) {
    for (const key of Object.keys(items)) {
      // eslint-disable-next-line react-hooks/refs
      itemsRef.current[key] = items[key]
    }
  }

  // Stabilize callbacks in refs so SelectBase.Root doesn't see new
  // function references on every render, which would cause store updates.
  // Synced via an effect (not read during render), unlike itemsRef above:
  // these refs are only read later, from stableOnValueChange/
  // stableOnOpenChange in response to a user interaction, so there's no
  // same-render-pass requirement forcing a direct render-time write.
  const onValueChangeRef = React.useRef(onValueChange)
  const onOpenChangeRef = React.useRef(onOpenChange)
  React.useEffect(() => {
    onValueChangeRef.current = onValueChange
    onOpenChangeRef.current = onOpenChange
  }, [onValueChange, onOpenChange])

  const stableOnValueChange = React.useMemo(
    () => (val: string) => onValueChangeRef.current?.(val),
    []
  )

  const stableOnOpenChange = React.useMemo(
    () => (o: boolean) => onOpenChangeRef.current?.(o),
    []
  )

  return (
    <SelectItemsRefContext.Provider value={itemsRef}>
      <SelectBase.Root
        data-slot="select"
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange ? stableOnValueChange : undefined}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange ? stableOnOpenChange : undefined}
        disabled={disabled}
        required={required}
        name={name}
        modal={false}
      >
        {children}
      </SelectBase.Root>
    </SelectItemsRefContext.Provider>
  )
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectBase.Group>) {
  return <SelectBase.Group data-slot="select-group" {...props} />
}

function SelectValue({
  placeholder,
  ...props
}: React.ComponentProps<typeof SelectBase.Value> & {
  placeholder?: string
}) {
  const itemsRef = React.useContext(SelectItemsRefContext)
  const [tick, setTick] = React.useState(0)

  // Force one synchronous re-render after mount so we read item labels
  // that were registered via useLayoutEffect in SelectItem.
  React.useLayoutEffect(() => {
    if (tick === 0) setTick(1)
  }, [tick])

  return (
    <SelectBase.Value
      data-slot="select-value"
      placeholder={placeholder}
      {...props}
    >
      {(value: any) => {
        if (value == null) return null
        const label = itemsRef?.current?.[String(value)]
        return label ?? String(value)
      }}
    </SelectBase.Value>
  )
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<"button"> & {
  size?: "sm" | "default"
}) {
  return (
    <SelectBase.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectBase.Icon>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectBase.Icon>
    </SelectBase.Trigger>
  )
}

function SelectContent({
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  // `position` ("popper" | "item-aligned") is a Radix Select concept with no
  // Base UI equivalent — dead since the migration to @base-ui-components,
  // accepted but never able to do anything, so dropped rather than kept as
  // a misleading no-op. side/sideOffset/align are real Positioner props but
  // are hardcoded below (not yet wired to be caller-overridable); no caller
  // currently passes them, so leaving that as-is here rather than guessing
  // at a wiring nobody has needed yet.
  side?: "top" | "right" | "bottom" | "left"
  sideOffset?: number
  align?: "start" | "center" | "end"
}) {
  return (
    <SelectBase.Portal>
      <SelectBase.Positioner
        alignItemWithTrigger={false}
        side="bottom"
        sideOffset={4}
        className="z-[9999]"
      >
        <SelectBase.Popup
          data-slot="select-content"
          className={cn(
            "bg-popover text-popover-foreground data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 data-[ending-style]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--available-height) min-w-[8rem] origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className
          )}
          {...props}
        >
          <div className="p-1 w-full min-w-(--anchor-width) scroll-my-1">
            {children}
          </div>
        </SelectBase.Popup>
      </SelectBase.Positioner>
    </SelectBase.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectBase.GroupLabel>) {
  return (
    <SelectBase.GroupLabel
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  value,
  ...props
}: React.ComponentProps<typeof SelectBase.Item>) {
  const itemsRef = React.useContext(SelectItemsRefContext)

  // useLayoutEffect writes to the ref before paint — no state changes,
  // no re-renders, no interference with SelectBase.Root's internal store.
  React.useLayoutEffect(() => {
    if (itemsRef && value != null) {
      // Capture the map once: itemsRef.current is the same shared object
      // for the lifetime of the provider (mutated in place, never
      // replaced), but re-dereferencing itemsRef.current inside the
      // cleanup instead of using this captured reference is exactly what
      // react-hooks/exhaustive-deps warns about in general.
      const map = itemsRef.current
      map[String(value)] = children
      return () => {
        delete map[String(value)]
      }
    }
  }, [itemsRef, value, children])

  return (
    <SelectBase.Item
      data-slot="select-item"
      value={value}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectBase.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectBase.ItemIndicator>
      </span>
      <SelectBase.ItemText>{children}</SelectBase.ItemText>
    </SelectBase.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="select-separator"
      role="separator"
      className={cn(
        "bg-border pointer-events-none -mx-1 my-1 h-px",
        className
      )}
      {...props}
    />
  )
}

function SelectScrollUpButton() {
  return null
}

function SelectScrollDownButton() {
  return null
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
